import { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useCart } from '@/context/CartContext';
import { useAuth } from '@/context/AuthContext';
import api from '@/utils/api';
import { MapPin, Loader2, Crosshair, X, Truck, Clock, Store, AlertTriangle, Home, Briefcase, Tag } from 'lucide-react';
import { formatGs } from '@/utils/helpers';
import { MapContainer, TileLayer, useMapEvents, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import { toast } from 'sonner';

const ASUNCION_CENTER = [-25.2867, -57.6470];
const TILE_URL = 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png';
const LABEL_PRESETS = [
  { icon: Home, label: 'Casa' },
  { icon: Briefcase, label: 'Trabajo' },
  { icon: Tag, label: 'Otro' },
];

function MapMoveHandler({ onMoveEnd }) {
  useMapEvents({
    moveend(e) {
      const c = e.target.getCenter();
      onMoveEnd(c.lat, c.lng);
    },
  });
  return null;
}

function FlyToCenter({ center }) {
  const map = useMap();
  useEffect(() => { map.flyTo(center, map.getZoom(), { duration: 0.6 }); }, [center, map]);
  return null;
}

export default function AddressModal({ open, onClose }) {
  const { setZone, setUserLocation, zone } = useCart();
  const { isAuthenticated } = useAuth();
  const [flyTarget, setFlyTarget] = useState(null);
  const [validating, setValidating] = useState(false);
  const [geoLoading, setGeoLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [step, setStep] = useState('map'); // 'map' | 'save'
  const [saveLabel, setSaveLabel] = useState('Casa');
  const [saveRef, setSaveRef] = useState('');
  const [saving, setSaving] = useState(false);
  const debounceRef = useRef(null);
  const initialCenter = useRef(null);

  if (!initialCenter.current) {
    const stored = zone ? [parseFloat(zone.lat || 0), parseFloat(zone.lng || 0)] : null;
    initialCenter.current = (stored && stored[0] !== 0) ? stored : ASUNCION_CENTER;
  }

  const validateZone = useCallback(async (lat, lng) => {
    setValidating(true);
    try {
      const { data } = await api.post('/zonas-entrega/validar-ubicacion', { lat, lng });
      setResult({ ...data, lat, lng });
    } catch {
      setResult({ dentro_de_zona: false, lat, lng });
    } finally {
      setValidating(false);
    }
  }, []);

  const handleMoveEnd = useCallback((lat, lng) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => validateZone(lat, lng), 400);
  }, [validateZone]);

  const handleGeolocate = () => {
    if (!navigator.geolocation) {
      toast.error('Tu navegador no soporta geolocalización.');
      return;
    }
    setGeoLoading(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setFlyTarget([pos.coords.latitude, pos.coords.longitude]);
        toast.success('Ubicación actual obtenida');
        setGeoLoading(false);
      },
      (err) => {
        setGeoLoading(false);
        if (err.code === 1) {
          toast.error('Permiso de ubicación denegado. Podés mover el mapa manualmente.');
        } else if (err.code === 3) {
          toast.error('Tiempo de espera agotado. Intentá de nuevo.');
        } else {
          toast.error('No se pudo obtener la ubicación. Mové el mapa manualmente.');
        }
      },
      { enableHighAccuracy: true, timeout: 15000 }
    );
  };

  const handleConfirmLocation = () => {
    if (!result?.dentro_de_zona) return;
    if (isAuthenticated) {
      setStep('save');
    } else {
      // Not logged in - just set zone directly
      setZone(result.zona);
      setUserLocation({ lat: result.lat, lng: result.lng });
      onClose();
    }
  };

  const handleSaveAddress = async () => {
    if (!result?.dentro_de_zona) return;
    setSaving(true);
    try {
      const { data } = await api.post('/direcciones', {
        label: (saveLabel || '').trim() || 'Mi ubicación',
        address_line: `${result.lat.toFixed(5)}, ${result.lng.toFixed(5)}`,
        address_text: saveRef.trim() || null,
        lat: result.lat,
        lng: result.lng,
      });
      // Update last_address_id
      const addrId = data?.direccion?.id;
      if (addrId) {
        api.put('/auth/perfil', { last_address_id: addrId }).catch(() => {});
      }
      setZone({ ...result.zona, address_id: addrId, address_label: saveLabel, address_text: saveRef.trim() || null });
      setUserLocation({ lat: result.lat, lng: result.lng });
      toast.success(`Dirección "${saveLabel}" guardada`);
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Error al guardar dirección');
    }
    setSaving(false);
  };

  useEffect(() => {
    if (open) { setResult(null); setFlyTarget(null); setStep('map'); setSaveLabel('Casa'); setSaveRef(''); }
  }, [open]);

  if (!open) return null;

  const inZone = result?.dentro_de_zona;
  const outZone = result && !result.dentro_de_zona;

  return createPortal(
    <div data-testid="address-modal" className="fixed inset-0 z-[9999] flex flex-col bg-zinc-950">
      <button
        data-testid="address-modal-close"
        onClick={onClose}
        className="absolute top-4 left-4 z-[600] w-10 h-10 rounded-full bg-white shadow-lg flex items-center justify-center hover:bg-zinc-100 transition-colors"
      >
        <X size={20} className="text-zinc-800" />
      </button>

      {step === 'map' && (
        <>
          {/* Map */}
          <div className="relative flex-1 min-h-0" data-testid="location-map">
            <MapContainer center={initialCenter.current} zoom={15} className="h-full w-full" zoomControl={false} attributionControl={false}>
              <TileLayer url={TILE_URL} />
              <MapMoveHandler onMoveEnd={handleMoveEnd} />
              {flyTarget && <FlyToCenter center={flyTarget} />}
            </MapContainer>
            {/* Center pin */}
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-[500]">
              <div className="flex flex-col items-center -mt-8">
                <svg width="40" height="48" viewBox="0 0 40 48" fill="none">
                  <ellipse cx="20" cy="44" rx="8" ry="3" fill="rgba(0,0,0,0.15)" />
                  <path d="M20 2C13.37 2 8 7.37 8 14c0 9 12 22 12 22s12-13 12-22c0-6.63-5.37-12-12-12z" fill="#E84C1C" stroke="#fff" strokeWidth="2" />
                  <circle cx="20" cy="14" r="5" fill="#fff" />
                </svg>
              </div>
            </div>
            {validating && (
              <div className="absolute top-4 left-0 right-0 flex justify-center z-[500]">
                <span className="flex items-center gap-2 text-xs bg-white/90 text-zinc-700 px-4 py-2 rounded-full shadow-md backdrop-blur-sm">
                  <Loader2 size={14} className="animate-spin text-[#E84C1C]" /> Verificando zona...
                </span>
              </div>
            )}
            <button
              data-testid="geolocate-btn"
              onClick={handleGeolocate}
              disabled={geoLoading}
              className="absolute bottom-4 right-4 z-[500] w-12 h-12 rounded-full bg-white shadow-lg flex items-center justify-center hover:bg-zinc-50 active:scale-95 transition-all disabled:opacity-50"
            >
              {geoLoading ? <Loader2 size={20} className="animate-spin text-[#E84C1C]" /> : <Crosshair size={20} className="text-[#E84C1C]" />}
            </button>
          </div>

          {/* Bottom sheet */}
          <div data-testid="location-bottom-sheet" className="shrink-0 bg-zinc-950 rounded-t-3xl -mt-6 relative z-[500] shadow-[0_-8px_32px_rgba(0,0,0,0.5)]">
            <div className="flex justify-center pt-3 pb-2"><div className="w-10 h-1 rounded-full bg-zinc-700" /></div>
            <div className="px-5 pb-6 space-y-3">
              {!result && !validating && (
                <div className="text-center py-3 space-y-3">
                  <MapPin size={20} className="mx-auto text-zinc-500 mb-2" />
                  <p className="text-sm text-zinc-400">Mové el mapa para elegir tu ubicación</p>
                  {'geolocation' in navigator && (
                    <button
                      data-testid="geolocate-banner-btn"
                      onClick={handleGeolocate}
                      disabled={geoLoading}
                      className="w-full flex items-center justify-center gap-2 py-3 rounded-2xl border border-zinc-700 text-zinc-300 hover:text-white hover:border-zinc-500 bg-zinc-900/60 transition-all active:scale-[0.98] disabled:opacity-50"
                    >
                      {geoLoading ? <Loader2 size={16} className="animate-spin text-[#E84C1C]" /> : <Crosshair size={16} className="text-[#E84C1C]" />}
                      <span className="text-sm font-medium">{geoLoading ? 'Obteniendo ubicación...' : 'Usar mi ubicación actual'}</span>
                    </button>
                  )}
                </div>
              )}
              {inZone && result.zona && (
                <>
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 rounded-xl bg-emerald-500/10 flex items-center justify-center shrink-0 mt-0.5">
                      <MapPin size={18} className="text-emerald-400" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-zinc-100">Entregamos en tu zona</p>
                      <p className="text-xs text-zinc-500 mt-0.5 font-mono">{result.lat.toFixed(5)}, {result.lng.toFixed(5)}</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    <div className="p-2.5 rounded-xl bg-zinc-900 border border-zinc-800/40 text-center">
                      <Store size={14} className="mx-auto text-[#E84C1C] mb-1" />
                      <p className="text-[10px] text-zinc-500">Sucursal</p>
                      <p className="text-xs font-medium truncate">{result.zona.branch_name}</p>
                    </div>
                    <div className="p-2.5 rounded-xl bg-zinc-900 border border-zinc-800/40 text-center">
                      <Truck size={14} className="mx-auto text-[#E84C1C] mb-1" />
                      <p className="text-[10px] text-zinc-500">Envío</p>
                      <p className="text-xs font-medium">{formatGs(result.zona.delivery_price)}</p>
                    </div>
                    <div className="p-2.5 rounded-xl bg-zinc-900 border border-zinc-800/40 text-center">
                      <Clock size={14} className="mx-auto text-[#E84C1C] mb-1" />
                      <p className="text-[10px] text-zinc-500">Tiempo</p>
                      <p className="text-xs font-medium">{result.zona.estimated_delivery_time_minutes || '~30'} min</p>
                    </div>
                  </div>
                </>
              )}
              {outZone && (
                <div className="flex items-start gap-3 p-3.5 rounded-xl bg-red-500/10 border border-red-500/20">
                  <AlertTriangle size={20} className="text-red-400 shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-semibold text-red-300">No realizamos entregas en esta zona</p>
                    <p className="text-xs text-zinc-400 mt-1">Mové el mapa hacia una zona cubierta</p>
                  </div>
                </div>
              )}
              <button
                data-testid="confirm-location-btn"
                onClick={handleConfirmLocation}
                disabled={!inZone || validating}
                className={`w-full py-3.5 rounded-2xl font-semibold text-sm flex items-center justify-center gap-2 transition-all active:scale-[0.98] ${
                  inZone ? 'bg-[#E84C1C] text-white shadow-[0_0_24px_rgba(232,76,28,0.35)] hover:bg-[#d43f12]' : 'bg-zinc-800 text-zinc-500 cursor-not-allowed'
                }`}
              >
                <MapPin size={18} />
                {outZone ? 'Fuera de zona' : inZone ? 'Confirmar ubicación' : 'Elegí una ubicación'}
              </button>
            </div>
          </div>
        </>
      )}

      {step === 'save' && (
        <div className="flex-1 flex flex-col justify-center px-5">
          <div className="bg-zinc-900 border border-zinc-800/50 rounded-2xl p-6 space-y-5 max-w-md mx-auto w-full">
            <div className="text-center">
              <div className="w-14 h-14 rounded-2xl bg-[#E84C1C]/10 flex items-center justify-center mx-auto mb-3">
                <MapPin size={24} className="text-[#E84C1C]" />
              </div>
              <h3 className="text-lg font-bold">Guardar dirección</h3>
              <p className="text-xs text-zinc-500 mt-1">Nombra esta ubicación para pedidos futuros</p>
            </div>

            {/* Label selector */}
            <div>
              <label className="text-xs text-zinc-400 mb-2 block">Nombre / Etiqueta</label>
              <div className="flex gap-2 mb-2">
                {LABEL_PRESETS.map(({ icon: Icon, label }) => (
                  <button
                    key={label}
                    data-testid={`save-label-${label.toLowerCase()}`}
                    onClick={() => setSaveLabel(label === 'Otro' ? '' : label)}
                    className={`flex-1 flex items-center justify-center gap-1.5 py-3 rounded-xl border transition-all ${
                      (label === 'Otro' ? !['Casa', 'Trabajo'].includes(saveLabel) : saveLabel === label)
                        ? 'bg-[#E84C1C]/15 border-[#E84C1C]/40 text-[#E84C1C]'
                        : 'bg-zinc-950 border-zinc-800 text-zinc-400 hover:border-zinc-600'
                    }`}
                  >
                    <Icon size={16} />
                    <span className="text-sm font-medium">{label}</span>
                  </button>
                ))}
              </div>
              {!['Casa', 'Trabajo'].includes(saveLabel) && (
                <input
                  data-testid="save-label-custom"
                  value={saveLabel}
                  onChange={e => setSaveLabel(e.target.value)}
                  placeholder="Ej: Oficina, Mamá, Gym..."
                  maxLength={50}
                  className="w-full bg-zinc-950 border border-zinc-800 rounded-xl px-3 py-2.5 text-sm placeholder:text-zinc-600 focus:border-[#E84C1C]/50 focus:outline-none"
                />
              )}
            </div>

            {/* Required reference / address text */}
            <div>
              <label className="text-xs text-zinc-400 mb-1 block">Dirección escrita <span className="text-[#E84C1C]">*</span></label>
              <input
                data-testid="save-address-ref"
                value={saveRef}
                onChange={e => setSaveRef(e.target.value)}
                placeholder="Ej: Andrade 223 c/ España, casa azul"
                className="w-full bg-zinc-950 border border-zinc-800 rounded-xl px-3 py-2.5 text-sm placeholder:text-zinc-600 focus:border-[#E84C1C]/50 focus:outline-none"
              />
              {!saveRef.trim() && <p className="text-[10px] text-amber-400 mt-1">Ingresá la dirección para que el repartidor te encuentre.</p>}
            </div>

            <div className="flex gap-2">
              <button
                onClick={() => setStep('map')}
                className="flex-1 py-3 rounded-xl border border-zinc-800 text-zinc-400 text-sm font-medium hover:bg-zinc-800/50 transition-colors"
              >
                Volver al mapa
              </button>
              <button
                data-testid="save-address-confirm"
                onClick={handleSaveAddress}
                disabled={saving || !saveRef.trim()}
                className="flex-1 py-3 rounded-xl bg-[#E84C1C] text-white text-sm font-semibold hover:bg-[#d43f12] transition-all disabled:opacity-50"
              >
                {saving ? <Loader2 size={16} className="animate-spin mx-auto" /> : 'Guardar y usar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>,
    document.body
  );
}
